import { useState, useRef } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { Check, MessageCircle } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { SOLUTIONS, PROCESS, waLink } from "../../lib/data";

export const Solutions = () => {
  const [active, setActive] = useState(0);
  const s = SOLUTIONS[active];

  const lineRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: lineRef, offset: ["start center", "end center"] });
  const scaleY = useSpring(scrollYProgress, { stiffness: 80, damping: 20 });

  return (
    <>
      <section id="solusi" className="py-24 md:py-32 bg-[#071A33] relative overflow-hidden">
        <div className="absolute inset-0 opacity-40 [background:radial-gradient(50%_50%_at_80%_0%,rgba(22,199,232,0.25),transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl">
            <Reveal><Overline>Solusi</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-heading font-bold text-white text-4xl md:text-5xl tracking-tight">
                Solusi untuk Berbagai Kebutuhan
              </h2>
            </Reveal>
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            {SOLUTIONS.map((it, i) => (
              <button
                key={it.key}
                onClick={() => setActive(i)}
                data-testid={`solution-tab-${it.key}`}
                className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-all ${
                  active === i
                    ? "bg-gradient-to-r from-[#0B63F6] to-[#16C7E8] text-white shadow-lg shadow-[#0B63F6]/30"
                    : "bg-white/5 text-white/70 border border-white/10 hover:bg-white/10"
                }`}
              >
                <it.icon className="h-4 w-4" />
                {it.label}
              </button>
            ))}
          </div>

          <div className="mt-10 grid lg:grid-cols-2 gap-8 items-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={s.key + "-img"}
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="relative rounded-[2rem] overflow-hidden border border-white/10 aspect-[4/3]"
              >
                <img src={s.img} alt={`Solusi teknologi untuk ${s.label}`} loading="lazy" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#071A33]/80 to-transparent" />
              </motion.div>
            </AnimatePresence>

            <AnimatePresence mode="wait">
              <motion.div
                key={s.key + "-body"}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
              >
                <h3 className="font-heading font-bold text-white text-3xl">{s.label}</h3>
                <ul className="mt-6 space-y-3">
                  {s.items.map((it) => (
                    <li key={it} className="flex items-center gap-3 text-white/80">
                      <span className="grid place-items-center h-6 w-6 rounded-full bg-[#16C7E8]/15 text-[#16C7E8]">
                        <Check className="h-3.5 w-3.5" />
                      </span>
                      {it}
                    </li>
                  ))}
                </ul>
                <a
                  href={waLink(s.wa)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`solution-cta-${s.key}`}
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-[#071A33] font-semibold px-6 py-3.5 hover:bg-[#16C7E8] hover:text-white transition-colors"
                >
                  <MessageCircle className="h-4 w-4" />
                  {s.cta}
                </a>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Process timeline */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl">
            <Reveal><Overline>Proses Kerja</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-heading font-bold text-[#111827] text-4xl md:text-5xl tracking-tight">
                Cara Kami Bekerja
              </h2>
            </Reveal>
          </div>

          <div ref={lineRef} className="relative mt-14 pl-16">
            <div className="absolute left-[26px] top-2 bottom-2 w-[2px] bg-gray-200" />
            <motion.div
              style={{ scaleY, transformOrigin: "top" }}
              className="absolute left-[26px] top-2 bottom-2 w-[2px] bg-gradient-to-b from-[#0B63F6] to-[#16C7E8]"
            />
            <div className="space-y-10">
              {PROCESS.map((p, i) => (
                <Reveal key={p.no} delay={i * 0.06}>
                  <div className="relative" data-testid={`process-step-${p.no}`}>
                    <span className="absolute -left-16 grid place-items-center h-14 w-14 rounded-2xl bg-white border-2 border-[#0B63F6]/20 font-heading font-extrabold text-[#0B63F6] shadow-sm">
                      {p.no}
                    </span>
                    <h3 className="font-heading font-bold text-xl text-[#111827]">{p.title}</h3>
                    <p className="mt-1.5 text-[#6B7280] leading-relaxed">{p.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
